/**
 * Popular Matches Component
 * Displays match cards for popular sports (Cricket, Tennis, Football)
 * Card-based layout matching the design system
 */

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSportsMarkets } from '@/hooks/useSportsMarkets';
import { useBetting } from '@/Context/BettingContext';
import { useScreenSize } from '@/hooks/useScreenSize';
import { ErrorStatsvgIcon } from '@/components/icons';
import SportsSVGIcons from '@/components/sports/SportsSVGIcons';
import PromoCarousel from '../PromoCarousel/PromoCarousel';
import styles from './PopularMatches.module.css';

const PopularMatches = () => {
  const { addBet } = useBetting();
  const { isMobile } = useScreenSize();
  const navigate = useNavigate();

  // Fetch in-play matches for popular sports: Cricket (4), Tennis (2), Soccer (1)
  const { data: cricketInPlay = [], isLoading: cricketInPlayLoading } = useSportsMarkets('4', 'in-play');
  const { data: tennisInPlay = [], isLoading: tennisInPlayLoading } = useSportsMarkets('2', 'in-play');
  const { data: soccerInPlay = [], isLoading: soccerInPlayLoading } = useSportsMarkets('1', 'in-play');

  // Fetch today's matches for popular sports
  const { data: cricketToday = [], isLoading: cricketTodayLoading } = useSportsMarkets('4', 'today');
  const { data: tennisToday = [], isLoading: tennisTodayLoading } = useSportsMarkets('2', 'today');
  const { data: soccerToday = [], isLoading: soccerTodayLoading } = useSportsMarkets('1', 'today');

  // Only show loading on initial load (when no data exists yet)
  // Don't show loader if we have any data - show data instantly
  const hasAnyData = (cricketInPlay && cricketInPlay.length > 0) || 
                     (tennisInPlay && tennisInPlay.length > 0) || 
                     (soccerInPlay && soccerInPlay.length > 0) ||
                     (cricketToday && cricketToday.length > 0) || 
                     (tennisToday && tennisToday.length > 0) || 
                     (soccerToday && soccerToday.length > 0);
  
  const isLoading = !hasAnyData && (
    (cricketInPlayLoading && !cricketInPlay) || 
    (tennisInPlayLoading && !tennisInPlay) || 
    (soccerInPlayLoading && !soccerInPlay) ||
    (cricketTodayLoading && !cricketToday) || 
    (tennisTodayLoading && !tennisToday) || 
    (soccerTodayLoading && !soccerToday)
  );

  // Combine in-play and today's matches, prioritizing in-play
  const combineMatches = (inPlayMatches, todayMatches) => {
    const matchMap = new Map();
    
    // Add in-play matches first (they have priority)
    if (inPlayMatches && inPlayMatches.length > 0) {
      inPlayMatches.forEach(match => {
        const key = match.matchId || match.id || `${match.team1}-${match.team2}`;
        if (!matchMap.has(key)) {
          matchMap.set(key, match);
        }
      });
    }
    
    // Add today's matches if not already present
    if (todayMatches && todayMatches.length > 0) {
      todayMatches.forEach(match => {
        const key = match.matchId || match.id || `${match.team1}-${match.team2}`;
        if (!matchMap.has(key)) {
          matchMap.set(key, match);
        }
      });
    }
    
    return Array.from(matchMap.values());
  };

  // Group matches by sport
  const sportGroups = useMemo(() => {
    const groups = [];

    // Cricket - combine in-play and today
    const cricketMatches = combineMatches(cricketInPlay, cricketToday);
    if (cricketMatches && cricketMatches.length > 0) {
      groups.push({
        sportId: 'cricket',
        sportName: 'Cricket',
        matches: cricketMatches.slice(0, 2), // Show at least 2 matches
      });
    }

    // Tennis - combine in-play and today
    const tennisMatches = combineMatches(tennisInPlay, tennisToday);
    if (tennisMatches && tennisMatches.length > 0) {
      groups.push({
        sportId: 'tennis',
        sportName: 'Tennis',
        matches: tennisMatches.slice(0, 2),
      });
    }

    // Soccer/Football - combine in-play and today
    const soccerMatches = combineMatches(soccerInPlay, soccerToday);
    if (soccerMatches && soccerMatches.length > 0) {
      groups.push({
        sportId: 'soccer',
        sportName: 'Soccer',
        matches: soccerMatches.slice(0, 2),
      });
    }

    return groups;
  }, [cricketInPlay, cricketToday, tennisInPlay, tennisToday, soccerInPlay, soccerToday]);

  const formatDate = (date) => {
    if (!date) return '';
    const now = new Date();
    const matchDate = new Date(date);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const matchDay = new Date(matchDate.getFullYear(), matchDate.getMonth(), matchDate.getDate());
    
    if (matchDay.getTime() === today.getTime()) {
      return `Today, ${matchDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
    }
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (matchDay.getTime() === tomorrow.getTime()) {
      return `Tomorrow, ${matchDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
    }
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[matchDate.getDay()]} ${matchDate.getDate()} ${months[matchDate.getMonth()]}, ${matchDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
  };

  const handleOddsClick = (match, outcome, type, odds) => {
    if (match.isSuspended || odds === '--' || odds === 'Suspend') return;

    let selectionName = '';
    if (outcome === '1') {
      selectionName = match.team1;
    } else if (outcome === 'x') {
      selectionName = 'The Draw';
    } else if (outcome === '2') {
      selectionName = match.team2;
    }

    const betDetails = {
      matchId: match.matchId || match.id,
      matchTitle: match.matchTitle || `${match.team1} v ${match.team2}`,
      team1: match.team1,
      team2: match.team2,
      selectionName,
      category: outcome,
      type,
      odds,
      isBlue: type === 'back',
      stake: '',
      liability: '0.00',
      market: 'MO',
      marketName: 'Match Odds',
      eventid: match.eventid,
    };

    addBet(betDetails);
  };

  const getOddsValue = (match, outcome, type) => {
    if (!match.odds) return '--';
    if (outcome === '1') {
      return type === 'back' 
        ? (match.odds.one?.back?.price || '--')
        : (match.odds.one?.lay?.price || '--');
    } else if (outcome === 'x') {
      return type === 'back'
        ? (match.odds.x?.back?.price || '--')
        : (match.odds.x?.lay?.price || '--');
    } else if (outcome === '2') {
      return type === 'back'
        ? (match.odds.two?.back?.price || '--')
        : (match.odds.two?.lay?.price || '--');
    }
    return '--';
  };

  const handleMatchClick = (match) => {
    const matchId = match.matchId || match.id || match.eventid;
    if (!matchId) return;

    navigate(`/sports/markets/${matchId}`, {
      state: {
        matchData: {
          id: matchId,
          matchId: matchId,
          eventid: match.eventid || matchId,
          eventId: match.eventid || matchId,
          team1: match.team1,
          team2: match.team2,
          matchTitle: `${match.team1} v ${match.team2}`,
          status: match.status,
          isLive: match.isLive,
          isSuspended: match.isSuspended || false,
          odds: match.odds,
          date: match.date,
          time: match.time,
          sportName: match.sportName,
          sportId: match.sportId,
        }
      }
    });
  };

  if (isLoading) {
    return (
      <div className={styles.popularMatches}>
        <div className={styles.loadingContainer}>
          <div className={styles.loader}>
            <span className={styles.loaderText}>loading</span>
            <span className={styles.load}></span>
          </div>
        </div>
      </div>
    );
  }

  if (sportGroups.length === 0) {
    return (
      <div className={styles.popularMatches}>
        <div className={styles.emptyState}>
          <ErrorStatsvgIcon size="152" className={styles.emptyBetsIcon} />
          <p className={styles.emptyStateText}>No matches available</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Promotional Carousel */}
      <PromoCarousel />

      <div className={styles.popularMatches}>
        {sportGroups.map((group) => (
        <div key={group.sportId} className={styles.leagueGroup}>
          {/* League Header */}
          <div className={styles.leagueHeader}>
            <div className={styles.leagueHeaderLeft}>
              <SportsSVGIcons 
                sportId={group.sportId} 
                size={isMobile ? "24px" : "28px"}
                className={styles.leagueIcon}
              />
              <h3 className={styles.leagueName}>{group.sportName}</h3>
            </div>
            <div className={styles.leagueHeaderRight}>
              {/* <span className={styles.expandIcon}>▲</span> */}
            </div>
          </div>

          {/* Match Cards */}
          <div className={styles.matchesList}>
            {group.matches.map((match) => (
              <div key={match.matchId || match.id} className={styles.matchCard}>
                {/* Match Info Section */}
                <div className={styles.matchInfo} onClick={() => handleMatchClick(match)}>
                  <div className={styles.matchDateTime}>
                    {formatDate(match.date)}
                  </div>
                  
                  <div className={styles.teamsContainer}>
                    {/* Team 1 */}
                    <div className={styles.teamRow}>
                      <div className={styles.teamLogo}>
                        <img 
                          src={`/assets/images/sports/${group.sportId}.png`} 
                          alt={match.team1}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                      <span className={styles.teamName} onClick={() => handleMatchClick(match)}>{match.team1}</span>
                    </div>

                    {/* Team 2 */}
                    <div className={styles.teamRow}>
                      <div className={styles.teamLogo}>
                        <img 
                          src={`/assets/images/sports/${group.sportId}.png`} 
                          alt={match.team2}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                      <span className={styles.teamName} onClick={() => handleMatchClick(match)}>{match.team2}</span>
                    </div>
                  </div>
                </div>

                {/* Odds Section */}
                <div className={styles.oddsSection}>
                  <div className={styles.oddsHeader}>
                    {/* <span className={styles.marketLabel}>1x2</span> */}
                    <div className={styles.oddsIcons}>
                      {/* <span className={styles.oddsIcon}>👕</span>
                      <span className={styles.oddsIcon}>☰</span> */}
                    </div>
                  </div>
                  
                  <div className={styles.oddsButtons}>
                    {/* Outcome 1 (Home Win) */}
                    <div className={styles.oddsButtonGroup}>
                      <label className={styles.oddsLabel}>1</label>
                      <div className={styles.oddsPair}>
                        <button
                          className={`${styles.oddsButton} ${styles.oddsButtonBack}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOddsClick(match, '1', 'back', getOddsValue(match, '1', 'back'));
                          }}
                        >
                          {getOddsValue(match, '1', 'back')}
                        </button>
                        <button
                          className={`${styles.oddsButton} ${styles.oddsButtonLay}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOddsClick(match, '1', 'lay', getOddsValue(match, '1', 'lay'));
                          }}
                        >
                          {getOddsValue(match, '1', 'lay')}
                        </button>
                      </div>
                    </div>

                    {/* Outcome X (Draw) */}
                    {match.odds?.x && (
                      <div className={styles.oddsButtonGroup}>
                        <label className={styles.oddsLabel}>draw</label>
                        <div className={styles.oddsPair}>
                          <button
                            className={`${styles.oddsButton} ${styles.oddsButtonBack}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOddsClick(match, 'x', 'back', getOddsValue(match, 'x', 'back'));
                            }}
                          >
                            {getOddsValue(match, 'x', 'back')}
                          </button>
                          <button
                            className={`${styles.oddsButton} ${styles.oddsButtonLay}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOddsClick(match, 'x', 'lay', getOddsValue(match, 'x', 'lay'));
                            }}
                          >
                            {getOddsValue(match, 'x', 'lay')}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Outcome 2 (Away Win) */}
                    <div className={styles.oddsButtonGroup}>
                      <label className={styles.oddsLabel}>2</label>
                      <div className={styles.oddsPair}>
                        <button
                          className={`${styles.oddsButton} ${styles.oddsButtonBack}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOddsClick(match, '2', 'back', getOddsValue(match, '2', 'back'));
                          }}
                        >
                          {getOddsValue(match, '2', 'back')}
                        </button>
                        <button
                          className={`${styles.oddsButton} ${styles.oddsButtonLay}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOddsClick(match, '2', 'lay', getOddsValue(match, '2', 'lay'));
                          }}
                        >
                          {getOddsValue(match, '2', 'lay')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      </div>
    </div>
  );
};

export default PopularMatches;
